/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import Autocomplete from "@/components/Autocomplete/Autocomplete";
import { DATE_FORMATS } from "@/shared";

import { useTimeInput } from "./useTimeInput";

interface TimeInputProps {
  startDateTime: string | null;
  isDatePickerOpen: boolean;
  isInputDisabled: boolean | undefined;
  inputError: {
    startDate: string;
  };
  handleTimeChange: (value: string) => void;
}

const TIME_INPUT_WIDTH = "72px";

const TimeInput = ({
  startDateTime,
  isDatePickerOpen,
  isInputDisabled,
  inputError,
  handleTimeChange
}: TimeInputProps) => {
  const {
    hoursValues,
    minutesValues,
    isHoursPickerOpen,
    isMinutesPickerOpen,
    hoursInputValue,
    minutesInputValue,
    timeFilterOptions,
    handleHoursBlur,
    handleHoursOpen,
    handleHoursClose,
    handleHoursInputChange,
    handleHoursChange,
    handleMinutesBlur,
    handleMinutesOpen,
    handleMinutesClose,
    handleMinutesInputChange,
    handleMinutesChange
  } = useTimeInput({ startDateTime, isDatePickerOpen, handleTimeChange });

  return (
    <div
      css={css({
        display: "flex",
        alignItems: "center",
        gap: "12px"
      })}
    >
      <Autocomplete
        data-testid="start-date-hours-time-input-field"
        size="small"
        isRequired
        hideRequiredStar
        freeSolo
        forcePopupIcon
        disableClearable
        width={TIME_INPUT_WIDTH}
        readOnly={isDatePickerOpen}
        open={isHoursPickerOpen}
        disabled={isInputDisabled}
        onOpen={handleHoursOpen}
        onClose={handleHoursClose}
        getOptionLabel={option => option?.label ?? option}
        options={hoursValues}
        onInputChange={handleHoursInputChange}
        onChange={handleHoursChange}
        inputValue={hoursInputValue}
        textFieldParams={{
          onBlur: (event: any) => handleHoursBlur(event.target.value),
          placeholder: DATE_FORMATS.Hours
        }}
        filterOptions={timeFilterOptions}
        customStyle={
          inputError.startDate && {
            marginBottom: "26px"
          }
        }
      />

      <span>:</span>

      <Autocomplete
        data-testid="start-date-minutes-time-input-field"
        size="small"
        isRequired
        hideRequiredStar
        freeSolo
        forcePopupIcon
        disableClearable
        width={TIME_INPUT_WIDTH}
        readOnly={isDatePickerOpen}
        open={isMinutesPickerOpen}
        disabled={isInputDisabled}
        onOpen={handleMinutesOpen}
        onClose={handleMinutesClose}
        getOptionLabel={option => option?.label ?? option}
        options={minutesValues}
        onInputChange={handleMinutesInputChange}
        onChange={handleMinutesChange}
        inputValue={minutesInputValue}
        textFieldParams={{
          onBlur: (event: any) => handleMinutesBlur(event.target.value),
          placeholder: DATE_FORMATS.Month
        }}
        filterOptions={timeFilterOptions}
        customStyle={
          inputError.startDate && {
            marginBottom: "26px"
          }
        }
      />
    </div>
  );
};

export default TimeInput;
